/**
 * StepIndicator - Visual step-by-step prompts for guided creation activities.
 *
 * Renders pulsing block highlights, ghost block outlines, step progress dots,
 * and celebration micro-animations. Designed for pre-readers (icon-only).
 *
 * @see GitHub Issue #852
 */

export default class StepIndicator {
    /**
     * @param {HTMLElement} container - Editor container
     * @param {Array} steps - Step definitions from activity scaffolding
     */
    constructor (container, steps) {
        this._container = container;
        this._steps = steps || [];
        this._element = null;
        this._progressDots = null;
        this._currentHighlight = null;
        this._ghostBlock = null;
        this._render();
    }

    _render () {
        this._element = document.createElement('div');
        this._element.className = 'activity-step-indicator';

        // Progress dots row (one per step)
        this._progressDots = document.createElement('div');
        this._progressDots.className = 'activity-step-dots';
        this._steps.forEach((step, i) => {
            const dot = document.createElement('span');
            dot.className = 'activity-step-dot';
            dot.dataset.stepIndex = i;
            this._progressDots.appendChild(dot);
        });
        this._element.appendChild(this._progressDots);

        // Current step icon display
        this._iconDisplay = document.createElement('div');
        this._iconDisplay.className = 'activity-step-icon-display';
        this._element.appendChild(this._iconDisplay);

        this._container.appendChild(this._element);
    }

    /**
     * Show indicator for a specific step.
     * @param {number} stepIndex
     */
    showStep (stepIndex) {
        const step = this._steps[stepIndex];
        if (!step) return;

        // Update progress dots
        this._updateDots(stepIndex);

        // Show step icon
        this._iconDisplay.textContent = '';
        this._iconDisplay.className = 'activity-step-icon-display activity-step-active';

        const icon = document.createElement('span');
        icon.className = `activity-step-icon activity-icon-${step.icon || 'default'}`;
        icon.textContent = this._getIconText(step.icon);
        this._iconDisplay.appendChild(icon);

        // Highlight target block in palette
        this._highlightPaletteBlock(step.targetBlock);

        // Show ghost block in scripting area
        this._showGhostBlock(step.targetBlock);
    }

    /**
     * Celebrate step completion with micro-animation.
     * @param {number} stepIndex
     * @param {string} celebrationType - 'sparkle' or 'confetti'
     */
    celebrateStep (stepIndex, celebrationType) {
        // Mark dot as complete
        const dot = this._progressDots.querySelector(`[data-step-index="${stepIndex}"]`);
        if (dot) {
            dot.classList.add('activity-step-dot-complete');
        }

        // Remove highlights
        this._clearHighlights();

        // Play celebration animation
        const celebration = document.createElement('div');
        celebration.className = `activity-step-celebration activity-celebration-${celebrationType}`;
        this._iconDisplay.appendChild(celebration);

        // Auto-remove celebration
        setTimeout(() => {
            if (celebration.parentNode) celebration.parentNode.removeChild(celebration);
        }, 800);
    }

    /**
     * Show free play prompt (after all guided steps complete).
     */
    showFreePlayPrompt () {
        this._clearHighlights();
        this._iconDisplay.textContent = '';
        this._iconDisplay.className = 'activity-step-icon-display activity-step-freeplay';

        // Star icon indicating creative freedom
        const icon = document.createElement('span');
        icon.className = 'activity-step-icon activity-icon-freeplay';
        icon.textContent = '\u2728'; // Sparkles emoji
        this._iconDisplay.appendChild(icon);
    }

    /**
     * Pulse the current step (for hint tier 0).
     */
    pulseCurrentStep () {
        this._iconDisplay.classList.add('activity-step-pulse');
        setTimeout(() => {
            this._iconDisplay.classList.remove('activity-step-pulse');
        }, 2000);
    }

    /**
     * Show explicit guide for a step (hint tier 2).
     * @param {number} stepIndex
     */
    showExplicitGuide (stepIndex) {
        const step = this._steps[stepIndex];
        if (!step) return;

        // Create a larger, more prominent highlight
        this._highlightPaletteBlock(step.targetBlock, true);
    }

    /**
     * Show all steps at once (walkthrough/give-up).
     */
    showAllSteps () {
        this._steps.forEach((step, i) => {
            const dot = this._progressDots.querySelector(`[data-step-index="${i}"]`);
            if (dot) dot.classList.add('activity-step-dot-revealed');
        });
    }

    /**
     * Reset to initial state.
     */
    reset () {
        this._clearHighlights();
        this._iconDisplay.textContent = '';
        this._iconDisplay.className = 'activity-step-icon-display';

        // Reset all dots
        const dots = this._progressDots.querySelectorAll('.activity-step-dot');
        dots.forEach(dot => {
            dot.classList.remove('activity-step-dot-complete', 'activity-step-dot-active', 'activity-step-dot-revealed');
        });
    }

    destroy () {
        this._clearHighlights();
        if (this._element && this._element.parentNode) {
            this._element.parentNode.removeChild(this._element);
        }
        this._element = null;
    }

    // --- Private ---

    _updateDots (activeIndex) {
        const dots = this._progressDots.querySelectorAll('.activity-step-dot');
        dots.forEach((dot, i) => {
            dot.classList.remove('activity-step-dot-active');
            if (i === activeIndex) {
                dot.classList.add('activity-step-dot-active');
            }
        });
    }

    _highlightPaletteBlock (blockType, prominent) {
        this._clearHighlights();

        // Find the block in the palette by type
        const selector = `[data-block-type="${blockType}"], .palette .block-${blockType}`;
        const paletteBlock = this._container.querySelector(selector);
        if (paletteBlock) {
            this._currentHighlight = document.createElement('div');
            this._currentHighlight.className = prominent
                ? 'activity-palette-highlight activity-palette-highlight-prominent'
                : 'activity-palette-highlight';

            const rect = paletteBlock.getBoundingClientRect();
            const containerRect = this._container.getBoundingClientRect();
            this._currentHighlight.style.left = `${rect.left - containerRect.left}px`;
            this._currentHighlight.style.top = `${rect.top - containerRect.top}px`;
            this._currentHighlight.style.width = `${rect.width}px`;
            this._currentHighlight.style.height = `${rect.height}px`;

            this._container.appendChild(this._currentHighlight);
        }
    }

    _showGhostBlock (blockType) {
        this._removeGhostBlock();

        this._ghostBlock = document.createElement('div');
        this._ghostBlock.className = `activity-ghost-block activity-ghost-${blockType}`;

        // Position in the scripting area
        const scripts = this._container.querySelector('.scripts');
        if (scripts) {
            scripts.appendChild(this._ghostBlock);
        }
    }

    _clearHighlights () {
        if (this._currentHighlight && this._currentHighlight.parentNode) {
            this._currentHighlight.parentNode.removeChild(this._currentHighlight);
            this._currentHighlight = null;
        }
        this._removeGhostBlock();
    }

    _removeGhostBlock () {
        if (this._ghostBlock && this._ghostBlock.parentNode) {
            this._ghostBlock.parentNode.removeChild(this._ghostBlock);
            this._ghostBlock = null;
        }
    }

    _getIconText (iconName) {
        const icons = {
            'arrow-right': '\u27A1',
            'arrow-left': '\u2B05',
            'arrow-up': '\u2B06',
            'arrow-down': '\u2B07',
            'arrow-up-bounce': '\u2B06',
            'loop': '\u21BB',
            'default': '\u2B50'
        };
        return icons[iconName] || icons['default'];
    }
}
